import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { 
    createUserWithEmailAndPassword, 
    updateProfile,
    GoogleAuthProvider,
    signInWithPopup
} from "firebase/auth";

import { auth, db } from '../Firebase';
import { getDoc, getDocs, setDoc, doc, query, collection, where } from "firebase/firestore";

const Register = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({ name: '', email: '', password: '', general: '' });
    const [loading, setLoading] = useState(false);
    
    const navigate = useNavigate();

    // Validate form inputs
    const validate = () => {
        const newErrors = { name: '', email: '', password: '' };
        let isValid = true;

        if (!name.trim()) {
            newErrors.name = "Name is required";
            isValid = false;
        }
        if (!email.trim()) {
            newErrors.email = "Email is required";
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = "Email is invalid";
            isValid = false;
        }
        if (!password.trim()) {
            newErrors.password = "Password is required";
            isValid = false;
        } else if (password.length < 8) {
            newErrors.password = "Password must be at least 8 characters long";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle manual registration
    const createUser = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        
        const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,16}$/;
        if (!passwordRegex.test(password)) {
            toast.error('Password must be 8-16 characters long, include at least one letter, one number, and one special character.');
            return;
        }
        
        try {
            setLoading(true);
            setErrors({ name: '', email: '', password: '', general: '' });

            // Create user in Firebase Auth
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Update profile with name
            await updateProfile(user, { displayName: name });

            // Store user data in Firestore
            await handleUserRegistration(user.uid, name, email, "User", user.photoURL);

            toast.success("User Registered Successfully!!", { position: 'top-center' });
            navigate('/email-verification');
        } catch (error) {
            console.error('Registration error:', error);
            toast.error(error.message, { position: 'bottom-center' });
        } finally {
            setLoading(false);
        }
    };

    // Handle Google Sign-In
    const handleGoogleSignIn = async () => {
        try {
            setLoading(true);
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Register the user in Firestore
            await handleUserRegistration(user.uid, user.displayName, user.email, "User", user.photoURL);

            toast.success("Successfully signed in with Google!", { position: 'top-center' });
            navigate('/dashboard');
        } catch (error) {
            console.error("Error signing in with Google:", error);
            toast.error(error.message, { position: 'bottom-center' });
        } finally {
            setLoading(false);
        }
    };

    // Register user data in Firestore
    const handleUserRegistration = async (uid, name, email, role, photoUrl) => {
        try {
            const userRef = doc(db, "Users", uid);
            const userSnap = await getDoc(userRef);
            
            if (!userSnap.exists()) {
                // Create or update the user document
                await setDoc(userRef, { 
                    name, 
                    email, 
                    role, 
                    photoUrl,
                    createdAt: new Date()
                });
                
                // Always populate contact history for all registration methods
                await populateContactHistory(uid);
            }
        } catch (error) {
            console.error("Error storing user data:", error);
            toast.error("Failed to save user data. Please try again.");
        }
    };

    // Populate contact history with just userId and an array of broker IDs (or empty array)
    const populateContactHistory = async (userId) => {
        try {
            // Query for brokers
            const brokersQuery = query(collection(db, "Users"), where("role", "==", "Broker"));
            const querySnapshot = await getDocs(brokersQuery);
            
            // Extract broker IDs or create empty array if none
            const brokerIds = querySnapshot.empty ? [] : querySnapshot.docs.map(doc => doc.id);
            
            // Create the contact history document
            const contactHistoryDoc = doc(db, "ContactHistory", userId);
            
            // Save the document with userId and brokerIds array
            await setDoc(contactHistoryDoc, {
                userId: userId,
                brokerIds: brokerIds,
                lastUpdated: new Date()
            });
            
            console.log(`Contact history created for user ${userId} with ${brokerIds.length} brokers`);
            
        } catch (error) {
            console.error("Error populating contact history:", error);
            toast.error("Failed to create contact records. Some features may be limited.");
            
            // Even if there's an error, try to create the collection with an empty document
            try {
                const contactHistoryDoc = doc(db, "ContactHistory", userId);
                await setDoc(contactHistoryDoc, {
                    userId: userId,
                    brokerIds: [],
                    lastUpdated: new Date()
                });
            } catch (secondaryError) {
                console.error("Failed to create fallback document:", secondaryError);
            }
        }
    };

    return (
        <div className="register-container">
            <div className="form-container">
                <h1 className="title">Get Started Now</h1>
    
                {errors.general && <div className="error-message">{errors.general}</div>}
    
                {/* Signup Form */}
                <form onSubmit={createUser} className="form">
                    <div className="input-group">
                        <label htmlFor="name" className="label">Name</label>
                        <input
                            id="name"
                            type="text"
                            placeholder="Enter your name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            className="input"
                        />
                        {errors.name && <div className="field-error">{errors.name}</div>}
                    </div>
    
                    <div className="input-group">
                        <label htmlFor="email" className="label">Email address</label>
                        <input
                            id="email"
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            className="input"
                        />
                        {errors.email && <div className="field-error">{errors.email}</div>}
                    </div>
    
                    <div className="input-group">
                        <label htmlFor="password" className="label">Password</label>
                        <input
                            id="password"
                            type="password"
                            placeholder="Create a password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            className="input"
                        />
                        {errors.password && <div className="field-error">{errors.password}</div>}
                    </div>
    
                    {/* Signup Button */}
                    <button 
                        type="submit" 
                        className={`button `}
                        disabled={loading}
                    >
                        <span className="button-text">
                            {loading ? 'Signing up...' : 'Sign Up'}
                        </span>
                    </button>
                </form>
    
                {/* Divider */}
                <div className="divider">
                    <span className="divider-text">Or</span>
                </div>
    
                {/* Google Sign-In */}
                <div className="social-buttons">
                    <button 
                        className={`google-button `}
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                    >
                        <img 
                            src="https://cdn1.iconfinder.com/data/icons/google-s-logo/150/Google_Icons-09-512.png" 
                            alt="Google logo" 
                            className="social-icon" 
                        />
                        <span className="button-text">
                            Sign in with Google
                        </span>
                    </button>
                </div>
    
                {/* Already Have an Account? */}
                <p className="login-link">
                    Already have an account? <Link to="/login" className="link">Sign In</Link>
                </p>
            </div>
    
            {/* Background Image Section */}
            <div className="image-container">
                <img 
                    src="https://plus.unsplash.com/premium_photo-1681487769650-a0c3fbaed85a?q=80&w=1910&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                    alt="Register background" 
                    className="background-image" 
                />
            </div>
            <ToastContainer />

            {/* CSS styles */}
            <style jsx>{`
                /* CSS Reset and Base Styles */
                * {
                    box-sizing: border-box;
                    margin: 0;
                    padding: 0;
                }

                /* Main Container */
                .register-container {
                    display: flex;
                    flex-direction: row;
                    min-height: 100vh;
                    background-color: #f0f2f5;
                }

                /* Form Container */
                .form-container {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    padding: 2rem;
                    background-color: #ffffff;
                }

                /* Image Container */
                .image-container {
                    display: none; /* Hidden on mobile by default */
                }

                /* Title */
                .title {
                    font-size: 28px;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    color: #333;
                    text-align: center;
                }

                /* Form */
                .form {
                    display: flex;
                    flex-direction: column;
                    gap: 1.25rem;
                }

                /* Input Groups */
                .input-group {
                    display: flex;
                    flex-direction: column;
                }

                /* Labels */
                .label {
                    font-size: 14px;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #333;
                }

                /* Inputs */
                .input {
                    padding: 0.75rem;
                    font-size: 16px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    transition: border-color 0.3s ease;
                }

                .input:focus {
                    outline: none;
                    border-color: #4CAF50;
                    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
                }

                /* Button */
                .button {
                    background-color: #4CAF50;
                    color: #fff;
                    border: none;
                    padding: 0.75rem;
                    font-size: 16px;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    margin-top: 0.5rem;
                    width: 100%;
                    position: relative;
                    min-width: 180px;
                    height: 40px; /* Minimum width to prevent shrinking */
                    white-space: nowrap; /* Prevents text wrapping */
                }

                .button:hover {
                    background-color: #45a049;
                }

                .button.loading {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                
                .button-text {
                    display: inline-block;
                    min-width: 90px; /* Ensures text has minimum width */
                }

                /* Divider */
                .divider {
                    display: flex;
                    align-items: center;
                    margin: 1.5rem 0;
                    position: relative;
                    text-align: center;
                }

                .divider::before, .divider::after {
                    content: '';
                    flex-grow: 1;
                    background-color: #ddd;
                    height: 1px;
                }

                .divider-text {
                    padding: 0 10px;
                    color: #666;
                    background-color: #fff;
                }

                /* Social Buttons */
                .social-buttons {
                    display: flex;
                    justify-content: center;
                    width: 100%;
                }

                .google-button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.75rem;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    background-color: #fff;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    width: 100%;
                    height: 40px;
                    min-width: 180px; /* Minimum width to prevent shrinking */
                    white-space: nowrap; /* Prevents text wrapping */
                }

                .google-button:hover {
                    background-color: #f5f5f5;
                }

                .google-button.loading {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .social-icon {
                    margin-right: 8px;
                    width: 24px;
                    height: 24px;
                    flex-shrink: 0; /* Prevents icon from shrinking */
                }

                /* Login Link */
                .login-link {
                    margin-top: 1.5rem;
                    font-size: 14px;
                    color: #666;
                    text-align: center;
                }

                .link {
                    color: #1890ff;
                    text-decoration: none;
                    font-weight: 600;
                }

                .link:hover {
                    text-decoration: underline;
                }

                /* Error Messages */
                .field-error {
                    color: #ff4d4f;
                    font-size: 14px;
                    margin-top: 4px;
                }

                .error-message {
                    background-color: #fff2f0;
                    border: 1px solid #ffccc7;
                    color: #ff4d4f;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 16px;
                }

                /* Tablet Styles */
                @media screen and (min-width: 768px) {
                    .form-container {
                        padding: 2rem 4rem;
                    }

                    .title {
                        font-size: 30px;
                        text-align: left;
                    }

                    .google-button {
                        padding: 0.75rem 1.5rem;
                    }

                    .social-icon {
                        width: 28px;
                        height: 28px;
                    }
                }

                /* Desktop Styles */
                @media screen and (min-width: 1024px) {
                    .image-container {
                        display: block; /* Show on desktop */
                        flex: 1;
                        overflow: hidden;
                        border-radius: 0 20px 20px 0;
                    }

                    .background-image {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    .form-container {
                        padding: 0 10%;
                        border-radius: 20px 0 0 20px;
                    }

                    .title {
                        font-size: 32px;
                    }
                }

                /* Large Desktop Styles */
                @media screen and (min-width: 1440px) {
                    .form-container {
                        padding: 0 15%;
                    }
                }
            `}</style>
        </div>
    );
};

export default Register;